import React, { useRef, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { Button } from '../ui';
import type { ConversationMessage } from '../../types/voice-demo';

function formatTimestamp(timestamp?: string | null): string {
  if (!timestamp) return '';
  try {
    return new Date(timestamp).toLocaleString([], {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  } catch {
    return '';
  }
}

interface MessageBubbleProps {
  message: ConversationMessage;
}

const MessageBubble: React.FC<MessageBubbleProps> = ({ message }) => {
  const isUser = message.role === 'human';

  return (
    <div className={`flex w-full ${isUser ? 'justify-end' : 'justify-start'}`}>
      <div
        className={`max-w-[78%] px-4 py-3 rounded-2xl whitespace-pre-wrap leading-relaxed ${
          isUser ? 'au-chat-bubble-user' : 'au-chat-bubble-ally'
        }`}
      >
        <div
          className={`flex justify-between gap-2.5 mb-1.5 text-[11px] font-bold uppercase tracking-wider ${
            isUser ? 'opacity-70' : 'text-au-text-tertiary'
          }`}
        >
          <span>{isUser ? 'You' : 'Ally'}</span>
          <span>{formatTimestamp(message.created_at || message.createdAt)}</span>
        </div>
        <div className="text-sm">{message.content}</div>
      </div>
    </div>
  );
};

interface ConversationPanelProps {
  messages: ConversationMessage[];
  isConnected: boolean;
  onSendMessage?: (message: string) => void;
  isSending?: boolean;
  /** Live AI response text being streamed (renders as a transient bubble after the persisted messages). */
  streamingText?: string;
  /** Whether an SSE response is currently in flight. */
  isStreaming?: boolean;
  /** Whether the SSE endpoint should be used instead of the regular one. */
  useStreaming?: boolean;
  /** Callback when the user toggles the streaming checkbox. */
  onToggleStreaming?: (next: boolean) => void;
  /**
   * Optional toolbar rendered above the conversation header. Only shown in full-screen
   * mode (in embedded mode the parent typically renders these same controls itself).
   */
  topToolbar?: React.ReactNode;
}

const ConversationPanel: React.FC<ConversationPanelProps> = ({
  messages,
  isConnected,
  onSendMessage,
  isSending = false,
  streamingText = '',
  isStreaming = false,
  useStreaming = false,
  onToggleStreaming,
  topToolbar,
}) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [inputValue, setInputValue] = useState('');
  const [isFullscreen, setIsFullscreen] = useState(false);
  const inputBusy = isSending || isStreaming;

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, streamingText, isStreaming]);

  // While in full-screen mode, lock body scroll and exit on Escape.
  useEffect(() => {
    if (!isFullscreen) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsFullscreen(false);
      }
    };

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isFullscreen]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = inputValue.trim();
    if (!trimmed || inputBusy || !onSendMessage) return;
    onSendMessage(trimmed);
    setInputValue('');
  };

  const showInput = !isConnected && onSendMessage;

  const messageCountLabel =
    messages.length > 0
      ? `${messages.length} message${messages.length !== 1 ? 's' : ''}`
      : isConnected
        ? 'Waiting...'
        : 'Not connected';

  const panel = (
    <div
      className={
        isFullscreen
          ? 'fixed inset-0 z-[9999] flex flex-col bg-[var(--au-surface)] p-6'
          : 'flex flex-col'
      }
    >
      {/* Top toolbar — only rendered while full-screen so the host page can keep
          its own controls in embedded mode. */}
      {isFullscreen && topToolbar && (
        <div className="mb-3">{topToolbar}</div>
      )}

      {/* Header */}
      <div className="flex items-center justify-between pb-3 mb-3 border-b border-au-divider">
        <h3 className="text-lg font-semibold text-au-text-primary">Conversation</h3>
        <div className="flex items-center gap-3">
          <span className="text-xs text-au-text-tertiary">{messageCountLabel}</span>
          <button
            type="button"
            onClick={() => setIsFullscreen((prev) => !prev)}
            aria-label={isFullscreen ? 'Exit full screen' : 'Enter full screen'}
            title={isFullscreen ? 'Exit full screen (Esc)' : 'Enter full screen'}
            className="flex items-center justify-center w-7 h-7 rounded text-au-text-secondary hover:text-au-text-primary hover:bg-au-surface-sunken transition-colors"
          >
            {isFullscreen ? (
              <svg
                className="w-4 h-4"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M9 9V4.5M9 9H4.5M9 9L3.75 3.75M9 15v4.5M9 15H4.5M9 15l-5.25 5.25M15 9h4.5M15 9V4.5M15 9l5.25-5.25M15 15h4.5M15 15v4.5M15 15l5.25 5.25"
                />
              </svg>
            ) : (
              <svg
                className="w-4 h-4"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M3.75 3.75v4.5m0-4.5h4.5m-4.5 0L9 9M3.75 20.25v-4.5m0 4.5h4.5m-4.5 0L9 15M20.25 3.75h-4.5m4.5 0v4.5m0-4.5L15 9m5.25 11.25h-4.5m4.5 0v-4.5m0 4.5L15 15"
                />
              </svg>
            )}
          </button>
        </div>
      </div>

      {/* Messages area */}
      <div
        ref={scrollRef}
        className={`flex flex-col gap-3.5 overflow-y-auto rounded-au-lg bg-au-surface-sunken p-4 ${
          isFullscreen
            ? 'flex-1 min-h-0'
            : showInput
              ? 'h-[400px]'
              : 'h-[460px]'
        }`}
      >
        {messages.length === 0 && !isStreaming ? (
          <div className="m-auto max-w-md text-center py-8">
            <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-au-bg-primary mb-3">
              <svg
                className="w-6 h-6 text-au-text-secondary"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={1.5}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z"
                />
              </svg>
            </div>
            <p className="text-au-text-secondary font-medium">
              {isConnected ? 'Conversation is empty' : 'No conversation loaded'}
            </p>
            <p className="text-sm text-au-text-tertiary mt-1">
              {isConnected
                ? 'Start speaking and the exchange will appear here.'
                : 'Click Connect to start a voice session, or type a message below.'}
            </p>
          </div>
        ) : (
          <>
            {messages.map((msg, idx) => (
              <MessageBubble key={idx} message={msg} />
            ))}
            {isStreaming && (
              <MessageBubble
                message={{
                  role: 'ai',
                  content: streamingText || '…',
                  created_at: new Date().toISOString(),
                }}
              />
            )}
          </>
        )}
      </div>

      {/* Text input bar — only when disconnected */}
      {showInput && (
        <div className="mt-3 space-y-2">
          {onToggleStreaming && (
            <label className="flex items-center gap-2 text-xs text-au-text-secondary select-none">
              <input
                type="checkbox"
                checked={useStreaming}
                onChange={(e) => onToggleStreaming(e.target.checked)}
                disabled={inputBusy}
                className="h-4 w-4 rounded border-au-divider text-au-action-primary focus:ring-au-action-primary"
              />
              <span>
                Stream response (SSE) — sends to{' '}
                <code className="font-mono">/api/v1/partner/chat/stream</code>
              </span>
            </label>
          )}

          {/* Input stays enabled while a response is in flight so the user
              can keep typing the next message; the submit button is the only
              thing that's disabled to prevent overlapping requests. */}
          <form onSubmit={handleSubmit} className="flex items-center gap-2">
            <input
              type="text"
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              placeholder={isStreaming ? 'Streaming response…' : 'Type a message...'}
              className="au-input flex-1"
            />
            <Button
              type="submit"
              variant="primary"
              size="sm"
              disabled={!inputValue.trim() || inputBusy}
              isLoading={inputBusy}
              aria-label="Send message"
            >
              <svg
                className="w-4 h-4"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M6 12L3.269 3.126A59.768 59.768 0 0121.485 12 59.77 59.77 0 013.27 20.876L5.999 12zm0 0h7.5"
                />
              </svg>
            </Button>
          </form>
        </div>
      )}
    </div>
  );

  return isFullscreen ? createPortal(panel, document.body) : panel;
};

export default ConversationPanel;
